import { useLazyQuery }                 from '@apollo/client'
import { debounce }                     from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector }     from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { LoadingEllipsis }              from '../../components/Loaders'
import { Button }                       from '../../components/Button'
import Pagination                       from '../../components/Pagination/Pagination'
import SearchBar                        from '../../components/Search/SearchBar'
import LocationsTable                   from '../../components/Locations/LocationsTable'
import { GET_LOCATIONS }                from '../../graphql/location'
import { setLocationsListRefetch }      from '../../features/user/currentUserSlice'
import { setURLParam }                  from '../../utilities'
import MainLayout                       from '../../layouts/MainLayout'

export default function Locations(): JSX.Element {
  const [ searchParams ]            = useSearchParams()
  const dispatch: any               = useDispatch()
  const navigate: any               = useNavigate()
  const currentUser: any            = useSelector( ( state: any ): any => state.currentUser.user )
  const locationsListRefetch: any   = useSelector( ( state: any ): any => state.currentUser.locationsListRefetch )
  const [ locations, setLocations ] = useState( null )
  const [ loaded, setLoaded ]       = useState( false )
  const [ query, setQuery ]         = useState( '' )

  const [ getLocations, { loading, error, refetch } ] = useLazyQuery( GET_LOCATIONS, {
    fetchPolicy:     locationsListRefetch ? 'network-only' : 'cache-first',
    nextFetchPolicy: locationsListRefetch ? 'network-only' : 'cache-first',
    onCompleted:     ( { getLocations }: any ): void => {
      dispatch( setLocationsListRefetch( false ) )
      setLocations( getLocations )
      setLoaded( true )
    },
    onError:         ( error: any ): void => console.log( error )
  } )

  const fetchLocations: any = async ( query: string, page: number ): Promise<any> => {
    await getLocations( { variables: { input: { query: ( query?.length > 0 ) ? query : null, page: ( page > 0 ) ? page : 1 } } } )
  }

  const debouncedSearch: any = useMemo(
    () =>
      debounce(
        async ( query: string, page: number ): Promise<any> =>
          await getLocations( { variables: { input: { query, page } } } ),
        2000,
        { leading: false, trailing: true }
      ),
    [ getLocations ]
  )

  const handleSearch: any = async ( value: string ): Promise<void> => {
    setURLParam( 'page', 1 )

    if ( value.length && value.trim().length > 0 ) {
      setQuery( value )
      await debouncedSearch( value, 1 )
    } else {
      setQuery( '' )
      await fetchLocations( null, 1 )
    }
  }

  const setCurrentPage: any = ( query: string, currentPage: number = 1 ): any => {
    setURLParam( 'page', currentPage )
    fetchLocations( query ?? null, currentPage )
  }

  useEffect( () => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [ debouncedSearch ] )

  useEffect( (): any => {
    let shouldFetch: boolean = true
    const pageParam: number  = !isNaN( parseInt( searchParams.get( 'page' ) ) ) ? parseInt( searchParams.get( 'page' ) ) : 1
    const queryParam: string = searchParams.get( 'q' )

    if ( shouldFetch && !loading && !loaded ) {
      if ( queryParam ) {
        setQuery( queryParam )
      } else {
        setQuery( '' )
      }

      fetchLocations( queryParam, pageParam )
    }

    return (): void => {
      shouldFetch = false
    }
  }, [ loaded ] )

  return (
    <MainLayout>
      <div className="max-w-screen-3xl mx-auto w-full">
        <div className="flex-grow lg:p-8 p-3 space-y-4 lg:space-y-8">
          { loading && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4 mt-4 w-full" /> }

          { !loading && currentUser?.admin && locations?.data &&
            <>
              <div className="row flex items-center w-full bg-white rounded-3xl p-1.5 2xl:py-2 2xl:px-4 mb-6 text-blue-input">
                <SearchBar
                  loading={ loading }
                  results={ locations?.data ?? null }
                  pagination={ locations?.links ?? null }
                  onChange={ handleSearch }
                  setPage={ setCurrentPage }
                  placeholder="Search locations"
                  type="locations"
                />
              </div>

              <div className="flex justify-end w-full my-4">
                <div className="flex space-x-4">
                  <Button label="Add new location" yellow onClick={ (): any => navigate( '/locations/add' ) } />
                </div>
              </div>

              <div className="row flex flex-col items-center w-full bg-white rounded-3xl p-3 xl:p-4">
                <div className="col flex flex-col w-full">
                  <LocationsTable locations={ locations.data } refetch={ refetch } currentUser={ currentUser } />

                  { ( locations.links?.totalPages > 0 ) &&
                    <Pagination
                      className="justify-content-center"
                      currentPage={ locations.links.page }
                      totalCount={ locations.links.totalResults }
                      pageSize={ locations.links.perPage }
                      onPageChange={ ( page: any ): any => setCurrentPage( query, page ) }
                    />
                  }
                </div>
              </div>
            </>
          }
          { !currentUser?.admin &&
            <div className="row flex flex-col items-center w-full bg-white rounded-3xl p-3 xl:p-4">
              Access Denied!
            </div>
          }
          { error &&
            <div className="row flex flex-col items-center w-full bg-white rounded-3xl p-3 xl:p-4">
              <b>Error occurred when fetching Locations List:</b>
              { error.graphQLErrors.map( ( { message }: any, i: number ): any => (
                <div key={ i } className="flex w-full"><span>{ message }</span></div>
              ) ) }
            </div>
          }
        </div>
      </div>
    </MainLayout>
  )
}
